import { Schema } from "effect";
import { ChannelThreadAnalyticsHandoffSource } from "./analytics";

// Email thread channel data (gmail, outlook integrations)
export const EmailThreadChannelData = Schema.Struct({
  subject: Schema.NullOr(Schema.String),
  // A cosmetically modified subject (e.g. set by a macro). When present it
  // should be shown in place of `subject`; the original `subject` is preserved.
  // Optional: thread creators don't set it — it's a later display-only override.
  displaySubject: Schema.optional(Schema.NullOr(Schema.String)),
  fromEmail: Schema.optionalWith(Schema.NullOr(Schema.String), {
    default: () => null,
  }),
});
export type EmailThreadChannelData = typeof EmailThreadChannelData.Type;

// Meta comment thread channel data (facebook, instagram integrations)
//
// `permalink` is the post permalink. `commentPermalink` is a deep link to the
// thread's top-level comment — only populated for Instagram, where the post
// permalink + `comment_id` query param does not work and a dedicated comment
// permalink must be fetched from the Graph API. Facebook leaves it null and
// derives the deep link client-side from `topLevelCommentId`.
export const MetaCommentThreadChannelData = Schema.Struct({
  previewUrl: Schema.NullOr(Schema.String),
  permalink: Schema.NullOr(Schema.String),
  topLevelCommentId: Schema.NullOr(Schema.String),
  // Optional (not required-nullable like its siblings) so producers that never
  // have a comment permalink — Facebook, and pre-column rows — don't have to
  // set it. Mirrors `displaySubject` on EmailThreadChannelData above.
  commentPermalink: Schema.optional(Schema.NullOr(Schema.String)),
});
export type MetaCommentThreadChannelData =
  typeof MetaCommentThreadChannelData.Type;

export const CHAT_FLOW_INPUT_STEP_TYPES = [
  "default",
  "collect_email",
  "code_verification",
  "offering_convert_to_email",
] as const;
export const ChatFlowInputStepType = Schema.Literal(
  ...CHAT_FLOW_INPUT_STEP_TYPES,
);
export type ChatFlowInputStepType = typeof ChatFlowInputStepType.Type;

export const ChatFlowThreadChannelData = Schema.Struct({
  lastKnownStepId: Schema.NullOr(Schema.Number),
  inputStepType: Schema.NullOr(ChatFlowInputStepType),
  fourDigitCodeId: Schema.NullOr(Schema.String),
});
export type ChatFlowThreadChannelData = typeof ChatFlowThreadChannelData.Type;

// SMS thread channel data (telnyx_sms, sms_phone_number, imessage integrations)
//
// Fields use absolute names (`customerPhoneNumber`, `merchantPhoneNumber`) so
// callers don't have to remember which direction "to" and "from" referred to
// in the inbound-perspective legacy shape. The decode side accepts the legacy
// `{toNumber, fromNumber}` keys so existing CRDB rows continue to load without
// a data backfill; encode always emits the new shape.
const SmsThreadChannelDataLegacy = Schema.Struct({
  toNumber: Schema.String,
  fromNumber: Schema.String,
});

const SmsThreadChannelDataNormalized = Schema.Struct({
  customerPhoneNumber: Schema.String,
  merchantPhoneNumber: Schema.String,
});

export const SmsThreadChannelData = Schema.transform(
  Schema.Union(SmsThreadChannelDataNormalized, SmsThreadChannelDataLegacy),
  SmsThreadChannelDataNormalized,
  {
    strict: true,
    decode: (raw) =>
      "customerPhoneNumber" in raw
        ? raw
        : {
            // Legacy: toNumber stored the merchant pool phone, fromNumber the customer.
            customerPhoneNumber: raw.fromNumber,
            merchantPhoneNumber: raw.toNumber,
          },
    encode: (value) => value,
  },
);
export type SmsThreadChannelData = typeof SmsThreadChannelData.Type;

export const MobileMessageThreadChannelData = Schema.Struct({
  customerNumber: Schema.String,
  merchantNumber: Schema.String,
});
export type MobileMessageThreadChannelData =
  typeof MobileMessageThreadChannelData.Type;

/**
 * Voice thread channel data — pinned to the (customer, merchant)
 * phone-number pair that owns the thread. Every voice call between
 * the same pair lands on this thread as another message; new pairs
 * spawn new threads.
 */
export const VoiceThreadChannelData = Schema.Struct({
  customerNumber: Schema.String,
  merchantNumber: Schema.String,
});
export type VoiceThreadChannelData = typeof VoiceThreadChannelData.Type;

// Union of all thread channel data types (use integrationType to distinguish)
// Integration type -> channel data mapping:
//   gmail, outlook -> EmailThreadChannelData
//   facebook, instagram -> MetaCommentThreadChannelData
//   redoChatFlow -> ChatFlowThreadChannelData
//   telnyx_sms -> SmsThreadChannelData
//   mobileMessage -> MobileMessageThreadChannelData
//   telnyx_voice -> VoiceThreadChannelData
//   tiktok, attentive, postscript, redoChatAi, redoChatLive, gorgias_sms, richpanel_sms -> null
export const ThreadChannelData = Schema.Union(
  EmailThreadChannelData,
  MetaCommentThreadChannelData,
  ChatFlowThreadChannelData,
  SmsThreadChannelData,
  MobileMessageThreadChannelData,
  VoiceThreadChannelData,
);
export type ThreadChannelData = typeof ThreadChannelData.Type;

// Thread analytics data (not channel-specific, any thread can have this)
export const ThreadAnalyticsData = Schema.Struct({
  handoffCategory: Schema.NullOr(Schema.String),
  handoffReason: Schema.NullOr(Schema.String),
  handoffSuggestedCategory: Schema.NullOr(Schema.String),
  handoffSource: Schema.NullOr(ChannelThreadAnalyticsHandoffSource),
  handoffSuggestedToolName: Schema.NullOr(Schema.String),
  handoffSuggestedToolEnabled: Schema.NullOr(Schema.Boolean),
});
export type ThreadAnalyticsData = typeof ThreadAnalyticsData.Type;
